import {
  Stack,
  Text,
  Group,
  Paper,
  Select,
  TextInput,
  ActionIcon,
  Tooltip,
  Flex,
  Button,
} from "@mantine/core"
import { IconX } from "@tabler/icons-react"
import { useState, useEffect, useCallback, useMemo } from "react"

import { AxesFeatureLabel } from "@costory/types/endpoints/labelsExplorer"

interface MapValuesStepProps {
  allValues: string[]
  onConfirm: (data: AxesFeatureLabel | null) => void
  completed?: AxesFeatureLabel
}

export function MapValues({
  allValues,
  onConfirm,
  completed,
}: MapValuesStepProps) {
  const [mappings, setMappings] = useState<Record<string, string>>({})
  const [customValues, setCustomValues] = useState<Record<string, boolean>>({})

  const dropdownOptions = useMemo(() => {
    const uniqueOptions = Array.from(new Set([...allValues]))
    return [...uniqueOptions.map((value) => ({ value, label: value }))]
  }, [allValues])

  const initializeMappings = useCallback(() => {
    if (completed?.maps) {
      const mappingsFromCompleted: Record<string, string> = {}
      completed.maps.forEach(({ value, to }) => {
        mappingsFromCompleted[value] = to
      })
      allValues.forEach((value) => {
        if (!mappingsFromCompleted[value]) {
          mappingsFromCompleted[value] = value
        }
      })
      return mappingsFromCompleted
    }

    const initialMappings: Record<string, string> = {}
    allValues.forEach((value) => {
      initialMappings[value] = value
    })
    return initialMappings
  }, [completed, allValues])

  useEffect(() => {
    setMappings(initializeMappings())
  }, [completed, allValues, initializeMappings])

  const createAxesFeatureLabel = useCallback(
    (currentMappings: Record<string, string>) => {
      const validMappings = Object.entries(currentMappings)
        .filter(([source, target]) => source && target && source !== target)
        .map(([value, to]) => ({ value, to }))

      return {
        name: "",
        columnsMerged: [],
        maps: validMappings,
      }
    },
    [],
  )

  const handleMappingChange = useCallback(
    (currentValue: string, newValue: string) => {
      const updatedMappings = {
        ...mappings,
        [currentValue]: newValue,
      }
      setMappings(updatedMappings)
    },
    [mappings],
  )

  const handleCustomValueToggle = useCallback((currentValue: string) => {
    setCustomValues((prev) => ({
      ...prev,
      [currentValue]: !prev[currentValue],
    }))
  }, [])

  const handleContinue = useCallback(() => {
    const axesFeatureLabel = createAxesFeatureLabel(mappings)
    onConfirm(axesFeatureLabel)
  }, [mappings, onConfirm, createAxesFeatureLabel])

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Map values to standardized names across all selected source labels
      </Text>

      <Paper
        withBorder
        radius="sm"
        bd="border"
        style={{
          maxHeight: "200px",
          overflowY: "auto",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Stack gap="xs">
          {Object.entries(mappings).map(([currentValue, newValue]) => (
            <Paper
              key={currentValue}
              withBorder
              p="xs"
              style={{ width: "100%", minWidth: 0 }}
            >
              <Group
                gap="xs"
                align="center"
                wrap="nowrap"
                style={{ width: "100%", minWidth: 0 }}
              >
                <Flex style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <Tooltip label={currentValue} withArrow>
                    <Text size="sm" truncate>
                      {currentValue}
                    </Text>
                  </Tooltip>
                </Flex>

                <Text size="xs" c="dimmed">
                  →
                </Text>
                <Flex style={{ flex: 1, minWidth: 0 }}>
                  {customValues[currentValue] ||
                  (!dropdownOptions.some((opt) => opt.value === newValue) &&
                    newValue !== "") ? (
                    <TextInput
                      placeholder="Enter custom value"
                      value={newValue}
                      onChange={(e) =>
                        handleMappingChange(currentValue, e.currentTarget.value)
                      }
                      style={{ width: "100%", minWidth: 0 }}
                      rightSection={
                        <Tooltip label="Close custom input">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              setCustomValues((prev) => ({
                                ...prev,
                                [currentValue]: false,
                              }))
                              handleMappingChange(currentValue, currentValue)
                            }}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Tooltip>
                      }
                    />
                  ) : (
                    <Select
                      placeholder="Select value"
                      data={dropdownOptions}
                      value={newValue}
                      onChange={(value) => {
                        if (value === "custom") {
                          handleCustomValueToggle(currentValue)
                          handleMappingChange(currentValue, "")
                        } else {
                          handleMappingChange(currentValue, value || "")
                        }
                      }}
                      searchable
                      style={{ width: "100%", minWidth: 0 }}
                    />
                  )}
                </Flex>
              </Group>
            </Paper>
          ))}
        </Stack>
      </Paper>

      <Button
        onClick={handleContinue}
        variant="filled"
        color="primary"
        size="sm"
        fullWidth
      >
        Continue
      </Button>
    </Stack>
  )
}
